// Planning : les 3 prochaines semaines (plus au besoin, « Voir 3 semaines de plus »), toutes mes équipes, jour par jour ;
// filtre Tout / Matchs / Entraînements retenu par l'appareil (Idries : « voir plus que 3 semaines au besoin et filtrer
// match / entraînement »).
// Pour chaque séance, la présence des miens d'un coup d'œil (attendu par
// défaut, D-026) et, pour le staff, les présences prévues.
// Bureau (D-101, tuile « Planning » du hub Club) : la semaine de toutes les équipes, avec leurs présences.
import { COMPETITION_LABEL } from '../domain/ffbb';
import { agree, displayName } from '../domain/labels';
import { genderOf, me, personaOf, squadName, type Ctx } from './context';
import { dayLabel, KIND_LABEL, time } from './format';
import { $, $$, html, mount } from './dom';
import { emptyGuide } from '../guide/empty';
import { openEventForm } from './event-form';
import { loadAgenda, type AgendaItem, type Presence } from './agenda';
import { fillCrests, oppCrest } from './crest';

type KindFilter = 'all' | 'match' | 'training';
const KIND_KEY = 'arceau.planning.kind';
const readKind = (): KindFilter => { try { const v = localStorage.getItem(KIND_KEY); return v === 'match' || v === 'training' ? v : 'all'; } catch { return 'all'; } };
const saveKind = (k: KindFilter) => { try { localStorage.setItem(KIND_KEY, k); } catch { /* sans stockage : le filtre vaut pour la visite */ } };
// Filtre par équipe (Idries : « ajoute le filtre par équipe ») : l'identifiant de l'équipe, vide = toutes.
const TEAM_KEY = 'arceau.planning.team';
const readTeam = (): string => { try { return localStorage.getItem(TEAM_KEY) ?? ''; } catch { return ''; } };
const saveTeam = (id: string) => { try { if (id) localStorage.setItem(TEAM_KEY, id); else localStorage.removeItem(TEAM_KEY); } catch { /* pour la visite */ } };
const MAX_DAYS = 26 * 7;   // une demi-saison au plus d'un coup
let extra = 0;             // semaines ajoutées par « Voir … de plus » (pour la visite)

export async function renderPlanning(root: HTMLElement, ctx: Ctx): Promise<void> {
  // Le bureau voit toutes les équipes : une semaine suffit (et reste légère à charger), une de plus à la fois.
  const bureau = personaOf(ctx) === 'bureau';
  const step = bureau ? 7 : 21;
  const days = Math.min(MAX_DAYS, step + extra * step);
  const { today, tomorrow, items: all } = await loadAgenda(ctx, days);
  const kind = readKind();
  // Les équipes présentes dans le planning (une équipe filtrée qui n'y est plus : toutes).
  const planTeams = [...new Map(all.filter(it => it.team).map(it => [it.team!.id, it.team!])).values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const teamF = planTeams.some(t => t.id === readTeam()) ? readTeam() : '';
  const items = all.filter(it => (kind === 'all' || it.ev.kind === kind) && (!teamF || it.team?.id === teamF));
  const self = me(ctx);
  const weeks = Math.round(days / 7);
  const moreLabel = bureau ? 'Voir une semaine de plus' : 'Voir 3 semaines de plus';

  const countChip = (it: AgendaItem) => {
    if (!it.counts) return '';
    const g = it.team?.gender ?? 'X';
    if (!it.counts.total) return html`<span class="chip chip-mute">effectif vide</span>`;
    return html`<span class="chip chip-count">${it.counts.present}/${it.counts.total} ${agree(g, 'présent', 'présente', it.counts.present)}</span>${it.counts.uncertain ? html` <span class="chip chip-warn">${it.counts.uncertain} ${agree(g, 'incertain', 'incertaine', it.counts.uncertain)}</span>` : ''}${it.counts.absent ? html` <span class="chip chip-ko">${it.counts.absent} ${agree(g, 'absent', 'absente', it.counts.absent)}</span>` : ''}`;
  };

  const chip = (pid: string, presence: Presence) => {
    const person = ctx.people.find(p => p.id === pid)!;
    const g = genderOf(ctx, pid);
    const who = ctx.people.length > 1 ? `${displayName(person)} · ` : '';
    if (presence === 'absent') return html`<span class="chip chip-ko">${who}${agree(g, 'Absent', 'Absente')}</span>`;
    if (presence === 'unavailable') return html`<span class="chip chip-mute">${who}Indispo</span>`;
    if (presence === 'uncertain') return html`<span class="chip chip-warn">${who}${agree(g, 'Incertain', 'Incertaine')}</span>`;
    if (presence === 'confirmed') return html`<span class="chip chip-ok">${who}${agree(g, 'Présent', 'Présente')} ✓</span>`;
    return html`<span class="chip chip-soft">${who}${agree(g, 'Attendu', 'Attendue')}</span>`;
  };

  const groups = new Map<string, AgendaItem[]>();
  for (const it of items) groups.set(it.occ.date, [...(groups.get(it.occ.date) ?? []), it]);

  mount(root, html`
    <section class="screen">
      <header class="screen-head head-row">
        <div>
          <p class="eyebrow">${bureau ? `Toutes les équipes · ${weeks > 1 ? `${weeks} semaines` : 'la semaine'}` : `${weeks} prochaines semaines`}</p>
          <h2>Planning</h2>
        </div>
        ${ctx.staffOf.size ? html`<button type="button" class="btn btn-primary btn-sm" id="b-new-ev" data-guide="plan-new">+ Programmer</button>` : ''}
      </header>
      ${ctx.teams.length && planTeams.length <= 1 ? html`
        <nav class="team-strip" aria-label="${bureau ? 'Les équipes' : 'Mes équipes'}">
          ${ctx.teams.map(t => html`<a class="team-pill" href="#/t/${t.id}">${t.name}${ctx.staffOf.has(t.id) && !bureau ? html` <span class="pill-tag">coach</span>` : ''}</a>`)}
        </nav>` : ''}
      <nav class="plk-filter" aria-label="Filtrer le planning">
        ${(['all', 'match', 'training'] as const).map(k => html`<button type="button" data-plk="${k}" aria-pressed="${String(kind === k)}">${k === 'all' ? 'Tout' : k === 'match' ? 'Matchs' : 'Entraînements'}</button>`)}
      </nav>
      ${planTeams.length > 1 ? html`<nav class="plk-filter plk-teams" aria-label="Filtrer par équipe">
        <button type="button" data-plt="" aria-pressed="${String(!teamF)}">Toutes les équipes</button>
        ${planTeams.map(tm => html`<button type="button" data-plt="${tm.id}" aria-pressed="${String(teamF === tm.id)}">${tm.name}</button>`)}
      </nav>` : ''}
      ${!items.length && all.length ? html`<p class="muted plk-none">${kind === 'match' ? 'Aucun match' : kind === 'training' ? 'Aucun entraînement' : 'Rien'}${teamF ? ` pour ${planTeams.find(x => x.id === teamF)?.name ?? 'cette équipe'}` : ''} sur ${weeks > 1 ? `ces ${weeks} semaines` : 'cette semaine'}.</p>` : ''}
      ${items.length ? [...groups].map(([date, list]) => html`
        <section class="day">
          <h3 class="day-head">${dayLabel(date, today, tomorrow)}</h3>
          ${list.map(it => {
            const { ev, occ: o, team, match } = it;
            const renfort = !team && it.mine.length > 0;
            return html`
              <a class="occ occ-${ev.kind}" href="#/e/${ev.id}/${o.date}" data-guide="plan-occ">
                <div class="occ-time"><strong>${time(o.start)}</strong>${o.end ? html`<span>${time(o.end)}</span>` : ''}</div>
                <div class="occ-body">
                  <div class="occ-title">${match ? html`${oppCrest(match, 'opp-crest-sm')}<span>${ev.title}</span>` : ev.title}</div>
                  <div class="occ-meta">
                    <span class="kind kind-${ev.kind}">${match ? COMPETITION_LABEL[match.competition] : KIND_LABEL[ev.kind]}</span>
                    ${match?.is_home === true ? html`<span class="kind kind-other">Dom.</span>` : match?.is_home === false ? html`<span class="kind kind-other">Ext.</span>` : ''}
                    ${team && (ctx.teams.length > 1 || squadName(ctx, match?.squad_id)) ? html`<span>${it.label}</span>` : ''}
                    ${renfort ? html`<span class="kind kind-renfort">Renfort</span>` : ''}
                    ${ev.location ? html`<span>${ev.location}</span>` : ''}
                  </div>
                  ${it.mine.length || it.counts ? html`<div class="occ-chips">${countChip(it)}${it.mine.map(m => chip(m.personId, m.presence))}</div>` : ''}
                </div>
                <span class="occ-go" aria-hidden="true">›</span>
              </a>`;
          })}
        </section>`) : all.length ? '' : html`
        ${bureau ? emptyGuide({ art: 'ti-cal', guide: 'plan-empty', title: 'Rien de prévu cette semaine',
            text: 'Les séances apparaissent ici dès que les coachs les programment ; les matchs officiels arrivent tout seuls des équipes reliées à la FFBB.' })
          : ctx.staffOf.size ? emptyGuide({ art: 'ti-cal', guide: 'plan-empty', title: 'Ta première séance en 1 minute', tour: 'programmer',
            text: 'Une fois, ou un créneau chaque semaine jusqu’à la fin de la saison : l’équipe est prévenue, et les présences se remplissent toutes seules.',
            foot: 'Les matchs officiels arrivent tout seuls quand l’équipe est reliée à la FFBB.' })
          : emptyGuide({ art: 'ti-cal', guide: 'plan-empty', title: 'Rien de prévu pour l’instant',
            text: `Les séances et les matchs apparaîtront ici dès que ton coach les aura programmés. ${self ? `Tu seras ${agree(genderOf(ctx, self.id), 'attendu', 'attendue')} par défaut.` : 'Chacun est attendu par défaut.'}` })}`}
      ${days < MAX_DAYS ? html`<button type="button" class="btn btn-ghost plk-more" id="plk-more">${moreLabel}</button>` : ''}
    </section>`);

  for (const b of $$<HTMLButtonElement>(root, '[data-plk]')) b.addEventListener('click', () => { saveKind(b.dataset.plk as KindFilter); void renderPlanning(root, ctx); });
  for (const b of $$<HTMLButtonElement>(root, '[data-plt]')) b.addEventListener('click', () => { saveTeam(b.dataset.plt ?? ''); void renderPlanning(root, ctx); });
  // L'équipe choisie reste en vue dans sa rangée.
  $(root, '[data-plt][aria-pressed="true"]')?.scrollIntoView({ inline: 'center', block: 'nearest' });
  $(root, '#plk-more')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement; b.disabled = true; b.textContent = 'Chargement…';
    const y = window.scrollY; extra += 1; await renderPlanning(root, ctx); window.scrollTo(0, y);
  });
  $(root, '#b-new-ev')?.addEventListener('click', () => openEventForm(ctx, () => { void renderPlanning(root, ctx); }));
  // Le logo des adversaires (annuaire des clubs, D-094), après l'affichage.
  void fillCrests(root);
}
